import { useCallback, useEffect, useState } from "react";
import { FaArrowRotateLeft } from "react-icons/fa6";
import { HiPencilAlt } from "react-icons/hi";
import { LuCopyPlus } from "react-icons/lu";
import { MdOutlinePayments } from "react-icons/md";
import { RiDeleteBin5Fill } from "react-icons/ri";
import { TbCoinFilled } from "react-icons/tb";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroCategoriaFinanceira from "../../components/modais/ModalCadastroCategoriaFinanceira";
import ModalCadastroCentroCusto from "../../components/modais/ModalCadastroCentroCusto";
import ModalCadastroCliente from "../../components/modais/ModalCadastroCliente";
import ModalCadastroFormaPagamento from "../../components/modais/ModalCadastroFormaPagamento";
import { api } from "../../services/api";
import {
  repositorioCategoriaFinanceira,
  repositorioCategoriaMestra,
  repositorioCentroCusto,
  repositorioCliente,
  repositorioContaReceber,
  repositorioFormaPagamento,
  repositorioNotaFiscal,
} from "../../sync/repositories/financeiro";

// Extrai uma mensagem legível de qualquer formato de erro que a API
// devolve (string, {detail}, {campo: [...]} etc.) — sem isso, erros de
// configuração (ex.: "Desconto não é permitido...") ficavam escondidos
// atrás de um "Erro ao salvar conta." genérico.
const extrairMensagemErro = (error) => {
  const dados = error.response?.data;
  if (!dados) return "Falha na conexão com o servidor.";
  if (typeof dados === "string") return dados;
  if (typeof dados.detail === "string") return dados.detail;
  const mensagens = [];
  const percorrer = (obj) => {
    if (typeof obj === "string") mensagens.push(obj);
    else if (Array.isArray(obj)) obj.forEach(percorrer);
    else if (obj && typeof obj === "object") Object.values(obj).forEach(percorrer);
  };
  percorrer(dados);
  return mensagens.join("\n") || "Erro ao processar a solicitação.";
};

// Pede o usuário autorizador quando uma alteração em conta já recebida
// exige autorização de supervisor (configuração de sistema). Reaproveita o
// endpoint de colaboradores já usado em Configurações → Usuários.
const solicitarAutorizacao = async () => {
  try {
    const resp = await api.get("/configuracoes/perfis-acesso/");
    const colaboradores = (resp.data?.results || resp.data || []).filter((c) => c.ativo);
    if (colaboradores.length === 0) {
      alert("Nenhum colaborador disponível para autorizar esta operação.");
      return null;
    }
    const lista = colaboradores.map((c) => `${c.usuario} - ${c.nome_completo || c.username}`).join("\n");
    const escolha = window.prompt(
      `Esta alteração requer autorização de um supervisor.\nDigite o ID do usuário autorizador:\n\n${lista}`
    );
    if (!escolha) return null;
    return Number(escolha);
  } catch {
    alert("Erro ao carregar lista de colaboradores para autorização.");
    return null;
  }
};

const AJUDA = {
  titulo: "Contas a Receber",
  descricao:
    "Registre aqui todo valor que a fazenda tem a receber de um cliente — desde a venda de produtos até serviços prestados. Cada lançamento pode ser dividido em parcelas, e o sistema calcula juros/multa/desconto e o status (pendente, parcial, recebida, atrasada, cancelada) automaticamente.",
  secoes: [
    {
      titulo: "Dados Básicos",
      campos: [
        {
          nome: "Cliente",
          obrigatorio: true,
          explicacao:
            "De quem a fazenda vai receber. Se o cliente ainda não existe, use o botão '+' ao lado do campo para cadastrá-lo sem sair desta tela.",
        },
        {
          nome: "Descrição",
          obrigatorio: false,
          explicacao: "Um resumo curto do que é essa receita (ex.: 'Venda de gado', 'Aluguel de máquina').",
        },
        {
          nome: "Documento",
          obrigatorio: false,
          explicacao: "Número da nota fiscal, contrato ou recibo relacionado.",
        },
        {
          nome: "Tipo de Conta",
          obrigatorio: false,
          explicacao: "'Variável' para receitas que mudam de valor a cada venda; 'Fixa' para receitas recorrentes de valor parecido todo mês.",
        },
        {
          nome: "Categoria Financeira / Centro de Custos",
          obrigatorio: false,
          explicacao: "Classificam a receita para os relatórios financeiros.",
        },
        {
          nome: "Valor",
          obrigatorio: true,
          explicacao: "Valor total da conta ANTES de juros, multa ou desconto — esses ajustes são aplicados depois, na seção 'Juros, Multa e Desconto'.",
        },
      ],
    },
    {
      titulo: "Datas",
      campos: [
        {
          nome: "Data de Emissão",
          obrigatorio: false,
          explicacao: "Data de emissão da nota/contrato de origem da receita.",
        },
        {
          nome: "Data de Vencimento",
          obrigatorio: true,
          explicacao: "Data limite para o recebimento. Se passar dessa data sem recebimento, o status muda automaticamente para 'Atrasada'.",
        },
      ],
    },
    {
      titulo: "Forma de Recebimento e Parcelas",
      campos: [
        {
          nome: "Forma de Recebimento",
          obrigatorio: false,
          explicacao:
            "Ao escolher uma forma já configurada, o sistema preenche automaticamente juros, multa e número de parcelas com os valores padrão dela.",
        },
        {
          nome: "Parcelamento (nº / total)",
          obrigatorio: false,
          explicacao: "Em quantas parcelas essa conta será dividida. Clique em 'Ver Parcelas' para gerar e revisar cada data e valor individualmente antes de salvar.",
        },
      ],
    },
    {
      titulo: "Juros, Multa e Desconto",
      campos: [
        {
          nome: "Juros / Multa / Desconto",
          obrigatorio: false,
          explicacao:
            "Podem ser lançados em R$ (valor fixo) ou em % sobre o valor total. O 'Valor Final da Conta', exibido no fim do formulário, já mostra o resultado com esses ajustes aplicados.",
        },
      ],
    },
  ],
  dicas: [
    "Diferente de Contas a Pagar, esta tela ainda não tem suporte completo a uso offline: a consulta funciona sem internet, mas cadastrar, editar, excluir, registrar recebimento e estornar exigem conexão.",
    "Ao criar uma conta com várias parcelas, cada parcela é salva como um lançamento próprio, ligado às demais pelo mesmo grupo — por isso, ao excluir, o sistema pergunta se é para excluir só aquela parcela ou o grupo inteiro.",
  ],
};

export default function GestaoContasAReceber() {
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [parcelasPreview, setParcelasPreview] = useState([]);
  const [mostrarModalRecebimento, setMostrarModalRecebimento] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  
  // Dados para os selects
  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [notas, setNotas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [formasRecebimento, setFormasRecebimento] = useState([]);
  const [categoriasMestras, setCategoriasMestras] = useState([]);

  // Cadastro rápido via modal (Cliente, Categoria, Centro de Custo, Forma de Recebimento)
  const [mostrarModalCliente, setMostrarModalCliente] = useState(false);
  const [mostrarModalCategoria, setMostrarModalCategoria] = useState(false);
  const [mostrarModalCentroCusto, setMostrarModalCentroCusto] = useState(false);
  const [mostrarModalFormaPagamento, setMostrarModalFormaPagamento] = useState(false);

  // Filtros
  const [filtros, setFiltros] = useState({
    cliente: "",
    busca: "",
    data_inicio: "",
    data_fim: "",
    status: "todos",
    valor_min: "",
    valor_max: ""
  });
  
  const [resultados, setResultados] = useState([]);
  const [resultadosFiltrados, setResultadosFiltrados] = useState([]);
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);
  
  const [conta, setConta] = useState({
    descricao: "",
    documento: "",
    valor_total: "",
    valor_recebido: "",
    juros: "0",
    multa: "0",
    desconto: "0",
    data_emissao: "",
    data_vencimento: "",
    cliente: "",
    categoria: "",
    centro_custo: "",
    nota_fiscal: "",
    numero_parcela: "1",
    total_parcelas: "1",
    tipo: "variavel",
    observacoes: "",
    juros_tipo: "valor",
    multa_tipo: "valor",
    desconto_tipo: "valor",
    forma_recebimento: "",
    data_recebimento: null
  });

  const [recebimento, setRecebimento] = useState({
    valor: "",
    data: new Date().toISOString().split("T")[0],
    forma_recebimento: "",
    observacao: "",
  });

  // Status options com cores
  const statusOptions = [
    { value: "todos", label: "Todas", cor: "bg-gray-100 text-gray-800" },
    { value: "pendente", label: "Pendente", cor: "bg-yellow-100 text-yellow-800" },
    { value: "parcial", label: "Parcial", cor: "bg-blue-100 text-blue-800" },
    { value: "recebida", label: "Recebida", cor: "bg-green-100 text-green-800" },
    { value: "atrasada", label: "Atrasada", cor: "bg-red-100 text-red-800" },
    { value: "cancelada", label: "Cancelada", cor: "bg-gray-100 text-gray-800" },
  ];

  // Tipos de conta
  const tiposConta = [
    { value: "variavel", label: "Variável", descricao: "Valor pode variar mês a mês" },
    { value: "fixa", label: "Fixa", descricao: "Valor fixo recorrente" }
  ];

  // Carregar dados iniciais
  const carregarDados = useCallback(async () => {
    try {
      const [fr, c, cat, n, cc, cm] = await Promise.all([
        repositorioFormaPagamento.listar(),
        repositorioCliente.listar(),
        repositorioCategoriaFinanceira.listar(),
        repositorioNotaFiscal.listar(),
        repositorioCentroCusto.listar(),
        repositorioCategoriaMestra.listar(),
      ]);

      setFormasRecebimento(Array.isArray(fr.data) ? fr.data : (fr.data.results || []));
      setClientes(Array.isArray(c.data) ? c.data : (c.data.results || []));
      setCategorias(Array.isArray(cat.data) ? cat.data : (cat.data.results || []));
      setNotas(Array.isArray(n.data) ? n.data : (n.data.results || []));
      setCentros(Array.isArray(cc.data) ? cc.data : (cc.data.results || []));
      setCategoriasMestras(Array.isArray(cm.data) ? cm.data : (cm.data.results || []));
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    }
  }, []);

  // Buscar contas
  const buscarContas = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const params = new URLSearchParams();
      
      if (filtros.cliente) params.append("cliente", filtros.cliente);
      if (filtros.status !== "todos") params.append("status", filtros.status);
      if (filtros.data_inicio) params.append("data_inicio", filtros.data_inicio);
      if (filtros.data_fim) params.append("data_fim", filtros.data_fim);
      if (filtros.busca) params.append("termo", filtros.busca);
      if (filtros.valor_min) params.append("valor_min", filtros.valor_min);
      if (filtros.valor_max) params.append("valor_max", filtros.valor_max);
      params.append("ordering", "-data_vencimento");
      
      const response = await repositorioContaReceber.listar(`?${params}`);
      const dados = response.data.results || response.data;
      setResultados(Array.isArray(dados) ? dados : []);
      setResultadosFiltrados(Array.isArray(dados) ? dados : []);
      setPaginaAtual(1);
    } catch (error) {
      console.error("Erro ao buscar contas:", error);
      alert("Erro ao carregar lista de contas");
    } finally {
      setCarregandoLista(false);
    }
  }, [filtros]);

  useEffect(() => {
    carregarDados();
    buscarContas();
  }, [carregarDados, buscarContas]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConta(prev => ({ ...prev, [name]: value }));
  };

  const selecionarForma = (id) => {
    const forma = formasRecebimento.find((f) => f.id == id);
    setConta(prev => ({
      ...prev,
      forma_recebimento: id,
      juros: forma?.juros_atraso || "0",
      multa: forma?.multa_atraso || "0",
      total_parcelas: forma?.permite_parcelamento ? forma?.max_parcelas || "1" : "1",
      numero_parcela: "1",
      data_vencimento: prev.data_emissao && forma?.prazo_dias
        ? new Date(new Date(prev.data_emissao).setDate(new Date(prev.data_emissao).getDate() + forma.prazo_dias)).toISOString().split("T")[0]
        : prev.data_vencimento,
    }));
  };

  const calcularValorFinal = () => {
    const total = Number(conta.valor_total) || 0;
    const juros = conta.juros_tipo === "porcentagem" ? total * (Number(conta.juros) / 100) : Number(conta.juros);
    const multa = conta.multa_tipo === "porcentagem" ? total * (Number(conta.multa) / 100) : Number(conta.multa);
    const desconto = conta.desconto_tipo === "porcentagem" ? total * (Number(conta.desconto) / 100) : Number(conta.desconto);
    return total + juros + multa - desconto;
  };

  const gerarPreviewParcelas = () => {
    if (!conta.data_emissao && !conta.data_vencimento) {
      alert("Informe a data de emissão ou data de vencimento primeiro.");
      return;
    }
    
    const forma = formasRecebimento.find(f => f.id == conta.forma_recebimento);
    const totalParcelas = Number(conta.total_parcelas) || 1;
    // valor_total guarda o valor ANTES de juros/multa/desconto — esses
    // ajustes são aplicados pelo backend ao calcular valor_final, então
    // dividir aqui o valor JÁ com desconto faria o desconto ser aplicado
    // duas vezes (uma na divisão, outra no cálculo do backend).
    const valorBase = (Number(conta.valor_total) || 0) / totalParcelas;
    const intervalo = forma?.intervalo_parcelas || 30;
    const prazoInicial = forma?.prazo_dias || 0;
    const lista = [];

    for (let i = 1; i <= totalParcelas; i++) {
      const dataBaseInicial = conta.data_emissao || conta.data_vencimento;
      if (!dataBaseInicial) return;
      const dataBase = new Date(dataBaseInicial);
      if (isNaN(dataBase)) return;
      const dias = prazoInicial + (i - 1) * intervalo;
      dataBase.setDate(dataBase.getDate() + dias);
      lista.push({
        numero: i,
        total: totalParcelas,
        valor: Number(valorBase.toFixed(2)),
        data_vencimento: dataBase.toISOString().split("T")[0],
      });
    }
    setParcelasPreview(lista);
  };

  const validarFormulario = () => {
    if (!conta.cliente) {
      alert("Selecione um cliente.");
      return false;
    }
    if (!conta.data_vencimento) {
      alert("Informe a data de vencimento.");
      return false;
    }
    if (!conta.valor_total || Number(conta.valor_total) <= 0) {
      alert("Informe um valor total válido.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;
    
    setLoading(true);
    const hoje = new Date().toISOString().split("T")[0];
    const valorFinal = calcularValorFinal();
    const valorRecebido = Number(conta.valor_recebido) || 0;

    let status = "pendente";
    if (valorRecebido === 0) status = "pendente";
    else if (valorRecebido < valorFinal) status = "parcial";
    else status = "recebida";
    if (status === "pendente" && conta.data_vencimento < hoje) status = "atrasada";

    try {
      if (editando) {
        const payload = {
          ...conta,
          status,
          juros: Number(conta.juros) || 0,
          multa: Number(conta.multa) || 0,
          desconto: Number(conta.desconto) || 0,
        };
        try {
          await api.put(`/financeiro/contas-a-receber/${editandoId}/`, payload);
        } catch (error) {
          if (error.response?.data?.autorizacao_necessaria) {
            const autorizadoPor = await solicitarAutorizacao();
            if (!autorizadoPor) return;
            await api.put(`/financeiro/contas-a-receber/${editandoId}/`, { ...payload, autorizado_por: autorizadoPor });
          } else {
            throw error;
          }
        }
        setMensagem("✅ Conta atualizada com sucesso!");
        handleCancel();
      } else {
        // valor_total enviado à API é o valor ANTES de juros/multa/desconto
        // — o backend aplica esses ajustes ao calcular valor_final. Usar
        // calcularValorFinal() aqui geraria desconto em dobro (uma vez na
        // gravação, outra no cálculo do backend).
        const valorBase = Number(conta.valor_total) || 0;
        const lista = parcelasPreview.length > 0 ? parcelasPreview : [{
          numero: 1, total: 1, valor: valorBase, data_vencimento: conta.data_vencimento
        }];
        
        const grupo = Date.now().toString();
        let idContaPai = null;
        
        for (let i = 0; i < lista.length; i++) {
          const p = lista[i];
          const novaConta = { 
            ...conta, 
            valor_recebido: 0, 
            status, 
            grupo_parcela: grupo, 
            numero_parcela: Number(p.numero), 
            total_parcelas: Number(p.total), 
            valor_total: Number(p.valor), 
            data_vencimento: p.data_vencimento, 
            conta_pai: idContaPai 
          };
          const response = await api.post("/financeiro/contas-a-receber/", novaConta);
          if (i === 0) idContaPai = response.data.id;
        }
        setMensagem("✅ Conta cadastrada com sucesso!");
        handleCancel();
      }
      
      buscarContas();
      setTimeout(() => setMensagem(""), 3000);
    } catch (error) {
      console.error("Erro:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (item) => {
    setConta({ ...item });
    setEditando(true);
    setEditandoId(item.id);
    setMostrarFormulario(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleNovo = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setConta({
      descricao: "", documento: "", valor_total: "", valor_recebido: "", juros: "0", multa: "0", desconto: "0",
      data_emissao: "", data_vencimento: "", cliente: "", categoria: "", centro_custo: "", nota_fiscal: "",
      numero_parcela: "1", total_parcelas: "1", tipo: "variavel", observacoes: "", juros_tipo: "valor",
      multa_tipo: "valor", desconto_tipo: "valor", forma_recebimento: "", data_recebimento: null
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
    setParcelasPreview([]);
    setMensagem("");
  };

  const handleExcluir = async (item) => {
    const escolha = window.prompt(
      "📌 Opções de exclusão:\n\n" +
      "1 - Excluir apenas esta parcela\n" +
      "2 - Excluir TODAS as parcelas deste grupo\n\n" +
      "Digite o número da opção desejada:"
    );
    if (!escolha) return;
    
    try {
      if (escolha === "1") {
        await api.delete(`/financeiro/contas-a-receber/${item.id}/`);
        alert("✅ Parcela excluída com sucesso!");
      } else if (escolha === "2") {
        await api.post("/financeiro/contas-a-receber/excluir-grupo/", { grupo_parcela: item.grupo_parcela });
        alert("✅ Grupo de parcelas excluído com sucesso!");
      } else {
        alert("Opção inválida");
        return;
      }
      buscarContas();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    }
  };

  const estornarRecebimento = async (item) => {
    if (!window.confirm("⚠️ Deseja estornar o recebimento desta conta?")) return;
    const justificativa = window.prompt("Justificativa do estorno (deixe em branco se não for exigida):") || "";
    try {
      await api.post(`/financeiro/contas-a-receber/${item.id}/estornar/`, { justificativa });
      alert("✅ Recebimento estornado com sucesso!");
      buscarContas();
    } catch (error) {
      console.error("Erro ao estornar:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    }
  };

  const confirmarRecebimento = async () => {
    if (!recebimento.valor || !recebimento.data) {
      alert("Preencha o valor e a data do recebimento.");
      return;
    }
    
    const valor = Number(recebimento.valor || 0);
    const restante = Number(contaSelecionada?.valor_restante || 0);
    
    if (valor <= 0) {
      alert("Valor inválido.");
      return;
    }
    
    if (valor > restante) {
      alert(`Valor maior que o restante (R$ ${restante.toFixed(2)})`);
      return;
    }
    
    try {
      await api.post(`/financeiro/contas-a-receber/${contaSelecionada.id}/receber/`, {
        valor, 
        data: recebimento.data, 
        forma_recebimento: recebimento.forma_recebimento, 
        observacao: recebimento.observacao
      });
      setMostrarModalRecebimento(false);
      setContaSelecionada(null);
      setRecebimento({ valor: "", data: new Date().toISOString().split("T")[0], forma_recebimento: "", observacao: "" });
      buscarContas();
      alert("✅ Recebimento registrado com sucesso!");
    } catch (error) {
      console.error("Erro ao receber:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    }
  };

  const aplicarFiltros = () => {
    let filtrados = [...resultados];

    if (filtros.data_inicio) {
      filtrados = filtrados.filter(
        c => c.data_vencimento >= filtros.data_inicio
      );
    }

    if (filtros.data_fim) {
      filtrados = filtrados.filter(
        c => c.data_vencimento <= filtros.data_fim
      );
    }

    if (filtros.valor_min) {
      filtrados = filtrados.filter(
        c => (c.valor_final || c.valor_total) >= Number(filtros.valor_min)
      );
    }

    if (filtros.valor_max) {
      filtrados = filtrados.filter(
        c => (c.valor_final || c.valor_total) <= Number(filtros.valor_max)
      );
    }

    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c =>
        c.descricao?.toLowerCase().includes(buscaLower) ||
        c.documento?.toLowerCase().includes(buscaLower) ||
        c.cliente_nome?.toLowerCase().includes(buscaLower)
      );
    }

    setResultadosFiltrados(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({ cliente: "", busca: "", data_inicio: "", data_fim: "", status: "todos", valor_min: "", valor_max: "" });
    setResultadosFiltrados(resultados);
    setPaginaAtual(1);
  };

  const formatarData = (data) => data ? new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—";
  const formatarMoeda = (valor) => `R$ ${Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];
  
  const totalFiltrado = resultadosFiltrados.reduce((acc, item) => acc + Number(item.valor_final || item.valor_total), 0);
  const totalPendente = resultadosFiltrados.reduce((acc, item) => acc + Number(item.valor_restante || 0), 0);
  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const resultadosPaginados = resultadosFiltrados.slice(indicePrimeiroItem, indiceUltimoItem);
  const totalPaginas = Math.ceil(resultadosFiltrados.length / itensPorPagina);

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Contas a Receber <MdOutlinePayments size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovo}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Conta a Receber
            </button>
          )}
        </div>

        {mensagem && (
          <div className="mb-4 p-3 rounded-lg bg-green-100 text-green-700 border border-green-200">
            {mensagem}
          </div>
        )}

        {/* Formulário de Cadastro/Edição - similar ao Contas a Pagar com "Cliente" no lugar de "Fornecedor" */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Conta a Receber" : "Nova Conta a Receber"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Seção 1: Dados Básicos */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                      <div className="flex items-center gap-2">
                        <select
                          name="cliente"
                          value={conta.cliente}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          required
                        >
                          <option value="">Selecione um cliente...</option>
                          {clientes.map(c => (
                            <option key={c.id} value={c.id}>{c.nome_completo || c.razao_social}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalCliente(true)}
                          title="Cadastrar novo cliente"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Cliente que deve pagar</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Descrição</label>
                      <input
                        type="text"
                        name="descricao"
                        value={conta.descricao}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: Venda de produtos, Serviço prestado..."
                      />
                      <span className="text-xs text-gray-500 mt-1">Identificação da receita</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Documento</label>
                      <input
                        type="text"
                        name="documento"
                        value={conta.documento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Nº da nota fiscal, contrato..."
                      />
                      <span className="text-xs text-gray-500 mt-1">Número do documento fiscal</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Conta</label>
                      <select
                        name="tipo"
                        value={conta.tipo}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      >
                        {tiposConta.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500 mt-1">{tiposConta.find(t => t.value === conta.tipo)?.descricao}</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Categoria Financeira</label>
                      <div className="flex items-center gap-2">
                        <select
                          name="categoria"
                          value={conta.categoria}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="">Selecione uma categoria...</option>
                          {categorias.map(c => (
                            <option key={c.id} value={c.id}>{c.nome}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalCategoria(true)}
                          title="Cadastrar nova categoria"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Centro de Custos</label>
                      <div className="flex items-center gap-2">
                        <select
                          name="centro_custo"
                          value={conta.centro_custo}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="">Selecione...</option>
                          {centros.map(c => (
                            <option key={c.id} value={c.id}>{c.nome}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalCentroCusto(true)}
                          title="Cadastrar novo centro de custos"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col w-48">
                      <label className="font-medium">Valor</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input type="number" placeholder="0,00"
                        value={conta.valor_total}
                        onChange={(e) => setConta({ ...conta, valor_total: e.target.value })}
                        className="w-full p-2 outline-none rounded-l"
                        />

                        <div className="h-6 w-px bg-green-500" />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">
                          R$
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção 2: Datas */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Data de Emissão</label>
                      <input
                        type="date"
                        name="data_emissao"
                        value={conta.data_emissao}
                        onChange={handleChange}
                        className="w-36 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                      <span className="text-xs text-gray-500 mt-1">Data de emissão da nota/contrato</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Data de Vencimento *</label>
                      <input
                        type="date"
                        name="data_vencimento"
                        value={conta.data_vencimento}
                        onChange={handleChange}
                        className="w-36 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      />
                      <span className="text-xs text-red-500 mt-1">Data limite para recebimento</span>
                    </div>
                  </div>
                </div>

                {/* Seção 3: Forma de Recebimento e Parcelas */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Forma de Recebimento</label>
                      <div className="flex items-center gap-2">
                        <select
                          value={conta.forma_recebimento || ""}
                          onChange={(e) => selecionarForma(e.target.value)}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="">Selecione...</option>
                          {formasRecebimento.map(f => (
                            <option key={f.id} value={f.id}>{f.nome}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalFormaPagamento(true)}
                          title="Cadastrar nova forma de recebimento"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Parcelamento</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          name="numero_parcela"
                          value={conta.numero_parcela}
                          onChange={handleChange}
                          className="w-20 border border-green-500 p-2 rounded text-center"
                          min="1"
                        />
                        <span className="text-gray-500">/</span>
                        <input
                          type="number"
                          name="total_parcelas"
                          value={conta.total_parcelas}
                          onChange={handleChange}
                          className="w-20 border border-green-500 p-2 rounded text-center"
                          min="1"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col justify-end">
                      <button
                        type="button"
                        onClick={gerarPreviewParcelas}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
                      >
                        Ver Parcelas
                      </button>
                    </div>
                  </div>

                  {parcelasPreview.length > 0 && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-semibold text-gray-700 mb-3">Preview das Parcelas</h5>
                      <div className="space-y-2">
                        {parcelasPreview.map((p, i) => (
                          <div key={i} className="flex flex-wrap gap-4 items-center p-2 bg-white rounded border">
                            <span className="font-medium w-16">Parcela {p.numero}/{p.total}</span>
                            <input
                              type="date"
                              value={p.data_vencimento}
                              onChange={(e) => {
                                const novas = [...parcelasPreview];
                                novas[i].data_vencimento = e.target.value;
                                setParcelasPreview(novas);
                              }}
                              className="border border-green-500 p-1 rounded"
                            />
                            <div className="flex items-center border border-green-500 rounded">
                              <span className="px-2 text-gray-700 bg-gray-100 rounded-l">R$</span>
                              <input
                                type="number"
                                step="0.01"
                                value={p.valor}
                                onChange={(e) => {
                                  const novas = [...parcelasPreview];
                                  novas[i].valor = Number(e.target.value);
                                  setParcelasPreview(novas);
                                }}
                                className="w-28 p-1 rounded-r"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Seção 4: Juros, Multa e Desconto - igual ao Contas a Pagar */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col w-36">
                      <label className="text-sm font-medium text-gray-700 mb-1">Juros</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          name="juros"
                          value={conta.juros}
                          onChange={handleChange}
                          className="w-24 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                        />
                        <div className="h-6 w-px bg-green-500" />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">
                          {conta.juros_tipo === "porcentagem" ? "%" : "R$"}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="juros_tipo" value="valor" checked={conta.juros_tipo === "valor"} onChange={handleChange} /> R$
                        </label>
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="juros_tipo" value="porcentagem" checked={conta.juros_tipo === "porcentagem"} onChange={handleChange} /> %
                        </label>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Aplicado em caso de atraso</span>
                    </div>

                    <div className="flex flex-col w-36">
                      <label className="text-sm font-medium text-gray-700 mb-1">Multa</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          name="multa"
                          value={conta.multa}
                          onChange={handleChange}
                          className="w-24 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                        />
                        <div className="h-6 w-px bg-green-500" />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">
                          {conta.multa_tipo === "porcentagem" ? "%" : "R$"}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="multa_tipo" value="valor" checked={conta.multa_tipo === "valor"} onChange={handleChange} /> R$
                        </label>
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="multa_tipo" value="porcentagem" checked={conta.multa_tipo === "porcentagem"} onChange={handleChange} /> %
                        </label>
                      </div>
                    </div>

                    <div className="flex flex-col w-36">
                      <label className="text-sm font-medium text-gray-700 mb-1">Desconto</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          name="desconto"
                          value={conta.desconto}
                          onChange={handleChange}
                          className="w-24 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                        />
                        <div className="h-6 w-px bg-green-500" />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">
                          {conta.desconto_tipo === "porcentagem" ? "%" : "R$"}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="desconto_tipo" value="valor" checked={conta.desconto_tipo === "valor"} onChange={handleChange} /> R$
                        </label>
                        <label className="flex items-center gap-1 text-sm">
                          <input type="radio" name="desconto_tipo" value="porcentagem" checked={conta.desconto_tipo === "porcentagem"} onChange={handleChange} /> %
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção 5: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={conta.observacoes}
                    onChange={handleChange}
                    rows={3}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre esta conta..."
                  />
                </div>

                {/* Valor Final */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-700">Valor Final da Conta:</span>
                    <span className="text-2xl font-bold text-green-600">{formatarMoeda(calcularValorFinal())}</span>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300 flex items-center gap-2"
                  >
                    {loading ? "💾 Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Filtros e Tabela - visíveis apenas quando formulário está fechado */}
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Cliente</label>
                  <select
                    value={filtros.cliente}
                    onChange={(e) => setFiltros({...filtros, cliente: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos os clientes</option>
                    {clientes.map(c => (
                      <option key={c.id} value={c.id}>{c.nome_completo || c.razao_social}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por termo</label>
                  <input
                    type="text"
                    placeholder="Descrição, documento ou cliente..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Valor Mínimo</label>
                  <div className="flex items-center border border-green-500 rounded">
                    <span className="px-2 text-gray-700 bg-gray-100 rounded-l">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      value={filtros.valor_min}
                      onChange={(e) => setFiltros({...filtros, valor_min: e.target.value})}
                      className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Valor Máximo</label>
                  <div className="flex items-center border border-green-500 rounded">
                    <span className="px-2 text-gray-700 bg-gray-100 rounded-l">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      value={filtros.valor_max}
                      onChange={(e) => setFiltros({...filtros, valor_max: e.target.value})}
                      className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                </div>
              </div>

              {/* Filtros rápidos por status */}
              <div className="flex flex-wrap gap-2 mt-4">
                {statusOptions.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setFiltros({...filtros, status: s.value})}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      filtros.status === s.value ? s.cor : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors flex items-center gap-2"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>

              <div className="mt-4 text-right">
                <span className="text-lg font-semibold text-gray-700">Total filtrado: </span>
                <span className="text-xl font-bold text-green-600">{formatarMoeda(totalFiltrado)}</span>
                <div className="text-sm text-gray-500">Pendente: {formatarMoeda(totalPendente)}</div>
              </div>
            </div>

            {/* Tabela de Contas */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
                <p className="text-gray-600">
                  {resultadosFiltrados.length} conta(s) encontrada(s)
                </p>
                <div className="text-sm text-gray-500">
                  Página {paginaAtual} de {totalPaginas || 1}
                </div>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">ID</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Cliente</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Vencimento</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Valor</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Restante</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Parcela</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                          <th className="p-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultadosPaginados.map(item => {
                          const statusInfo = getStatusInfo(item.status);
                          return (
                            <tr key={item.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                              <td className="p-3 text-sm font-mono">{item.id}</td>
                              <td className="p-3 text-sm">
                                {clientes.find(c => c.id == item.cliente)?.nome_completo ||
                                clientes.find(c => c.id == item.cliente)?.razao_social ||
                                "-"}
                              </td>
                              <td className="p-3 text-sm">{item.descricao || "-"}</td>
                              <td className="p-3 text-sm">{formatarData(item.data_vencimento)}</td>
                              <td className="p-3 text-sm font-medium">{formatarMoeda(item.valor_total_conta ?? item.valor_final ?? item.valor_total)}</td>
                              <td className="p-3 text-sm">{formatarMoeda(item.valor_restante)}</td>
                              <td className="p-3 text-sm text-center">{item.numero_parcela}/{item.total_parcelas}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.cor}`}>
                                  {statusInfo.label}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex justify-center gap-2">
                                  {item.valor_recebido > 0 && (
                                    <button
                                      onClick={() => estornarRecebimento(item)}
                                      className="text-gray-800 p-1 rounded hover:bg-yellow-100 transition-colors"
                                      title="Estornar recebimento"
                                    >
                                      <FaArrowRotateLeft size={23}/>
                                    </button>
                                  )}

                                  {item.status?.toLowerCase() !== "recebida" && (
                                    <button
                                      onClick={() => {
                                        setContaSelecionada(item);
                                        setRecebimento({
                                          valor: item.valor_restante,
                                          data: new Date().toISOString().split("T")[0],
                                          forma_recebimento: "",
                                          observacao: ""
                                        });
                                        setMostrarModalRecebimento(true);
                                      }}
                                      className="text-gray-800 p-1 rounded transition-colors hover:bg-green-100"
                                      title="Registrar recebimento"
                                    >
                                      <TbCoinFilled size={26} />
                                    </button>
                                  )}
                                  
                                  <button
                                    onClick={() => handleEditar(item)}
                                    className="text-gray-800 p-1 rounded hover:bg-yellow-100 transition-colors"
                                    title="Editar conta"
                                  >
                                    <HiPencilAlt size={26}/>
                                  </button>
                                  <button
                                    onClick={() => handleExcluir(item)}
                                    className="text-gray-800 p-1 rounded hover:bg-red-100 transition-colors"
                                    title="Excluir conta"
                                  >
                                    <RiDeleteBin5Fill size={26}/>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        
                        {resultadosPaginados.length === 0 && (
                          <tr>
                            <td colSpan="9" className="p-8 text-center text-gray-500">
                              {filtros.cliente || filtros.busca || filtros.status !== "todos" || filtros.data_inicio || filtros.data_fim
                                ? "Nenhuma conta encontrada com os filtros selecionados."
                                : "Nenhuma conta a receber cadastrada."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-center items-center gap-2">
                      <button
                        onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                        disabled={paginaAtual === 1}
                        className={`px-3 py-1 rounded transition-colors ${paginaAtual === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        ◀ Anterior
                      </button>
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(totalPaginas, 5) }, (_, i) => {
                          let num;
                          if (totalPaginas <= 5) num = i + 1;
                          else if (paginaAtual <= 3) num = i + 1;
                          else if (paginaAtual >= totalPaginas - 2) num = totalPaginas - 4 + i;
                          else num = paginaAtual - 2 + i;
                          return (
                            <button
                              key={num}
                              onClick={() => setPaginaAtual(num)}
                              className={`w-8 h-8 rounded transition-colors ${paginaAtual === num ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                            >
                              {num}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                        disabled={paginaAtual === totalPaginas}
                        className={`px-3 py-1 rounded transition-colors ${paginaAtual === totalPaginas ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        Próxima ▶
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Modal de Recebimento */}
        {mostrarModalRecebimento && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-[500px] max-w-[90%] p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Registrar Recebimento</h3>
                <button
                  onClick={() => setMostrarModalRecebimento(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor Pendente</label>
                  <div className="text-2xl font-bold text-green-600">
                    {formatarMoeda(contaSelecionada?.valor_restante)}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor a Receber *</label>
                  <div className="flex items-center border border-green-500 rounded">
                    <span className="px-3 text-gray-700 bg-gray-100 rounded-l">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={recebimento.valor}
                      onChange={(e) => setRecebimento({...recebimento, valor: e.target.value})}
                      className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                      placeholder="0,00"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data do Recebimento *</label>
                  <input
                    type="date"
                    value={recebimento.data}
                    onChange={(e) => setRecebimento({...recebimento, data: e.target.value})}
                    className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Recebimento *</label>
                  <select
                    value={recebimento.forma_recebimento}
                    onChange={(e) => setRecebimento({...recebimento, forma_recebimento: e.target.value})}
                    className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    required
                  >
                    <option value="">Selecione...</option>
                    {formasRecebimento.map(f => (
                      <option key={f.id} value={f.id}>{f.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                  <textarea
                    value={recebimento.observacao}
                    onChange={(e) => setRecebimento({...recebimento, observacao: e.target.value})}
                    className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    rows={2}
                    placeholder="Observações sobre este recebimento..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setMostrarModalRecebimento(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarRecebimento}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Confirmar Recebimento
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalCadastroCliente
        isOpen={mostrarModalCliente}
        onClose={() => setMostrarModalCliente(false)}
        onSuccess={async (novo) => {
          await carregarDados();
          setConta((prev) => ({ ...prev, cliente: novo.id }));
        }}
      />
      <ModalCadastroCategoriaFinanceira
        isOpen={mostrarModalCategoria}
        onClose={() => setMostrarModalCategoria(false)}
        categoriasMestras={categoriasMestras}
        onSuccess={async (nova) => {
          await carregarDados();
          setConta((prev) => ({ ...prev, categoria: nova.id }));
        }}
      />
      <ModalCadastroCentroCusto
        isOpen={mostrarModalCentroCusto}
        onClose={() => setMostrarModalCentroCusto(false)}
        onSuccess={async (novo) => {
          await carregarDados();
          setConta((prev) => ({ ...prev, centro_custo: novo.id }));
        }}
      />
      <ModalCadastroFormaPagamento
        isOpen={mostrarModalFormaPagamento}
        onClose={() => setMostrarModalFormaPagamento(false)}
        onSuccess={async (nova) => {
          await carregarDados();
          setConta((prev) => ({ ...prev, forma_recebimento: nova.id }));
        }}
      />
    </Layout>
  );
}